import { AddDrawingUseCase } from 'shared/domain/document/addDrawing';
import { DrawingChanges } from 'shared/domain/document/drawingChanges';
import { DrawingModel } from 'shared/domain/document/drawingModel';
import { DocumentDrawing } from 'shared/dtos/in/document';
import {
  HttpRequestModelType,
  HttpRequestStatus,
  __HttpRequestModel__,
} from 'shared/domain/httpRequest/httpRequestModel';
import { SyncStatus } from 'shared/domain/entitySyncStatus/syncStatus';
import { broadcastCreatedDrawing } from 'serviceWorker/broadcasts/documents';
import { getOne as getDocument, updateDrawing } from 'serviceWorker/db/documents';
import { add as addRequest } from 'serviceWorker/db/httpRequests';
import { get as getCurrentProject } from 'serviceWorker/db/selectedProject';
import { Identificable } from 'shared/types/commonView';
import { UploadStatus } from 'shared/types/uploadStatus';
import { debugLog } from 'shared/logger/debugLog';
import { nowISO } from 'shared/utils/date/dates';

function drawingToDocumentChanges(
  drawing: DrawingModel,
  existingDrawings: DocumentDrawing[] | undefined
): DrawingChanges {
  const modifiedAt = nowISO();
  if (drawing.clear && !drawing.data) {
    return {
      isDrawn: false,
      drawingSrc: '',
      mergedSrc: '',
      drawingSyncStatus: SyncStatus.PENDING,
      modifiedAt,
      drawings: [],
    };
  }

  // Backend contract (PT-6414): single drawing entry is replaced on re-draw.
  // Preserve the existing _id so upload/merge updates the same drawing record.
  const existingEntry = existingDrawings?.[existingDrawings.length - 1];

  return {
    isDrawn: true,
    drawingSrc: drawing.drawingImageUrl,
    mergedSrc: drawing.mergedImageUrl,
    thumbnailMergedSrc: drawing.thumbnailMergedImageUrl,
    fileType: drawing.blob?.type,
    drawingSyncStatus: SyncStatus.PENDING,
    modifiedAt,
    drawings: [
      {
        ...(existingEntry?._id ? { _id: existingEntry._id } : {}),
        drawnUploadStatus: UploadStatus.unknown,
        mergedUploadStatus: UploadStatus.unknown,
      },
    ],
  };
}

export class DrawingSaver implements AddDrawingUseCase {
  constructor(
    private getDocument: (localId: number) => Promise<{ drawings?: DocumentDrawing[] } | undefined>,
    private updateDrawing: (
      localId: number,
      changes: DrawingChanges
    ) => Promise<void>,
    private addRequest: (request: __HttpRequestModel__) => Promise<any>,
    private getCurrentProject: () => Promise<Identificable | undefined>,
    private broadcastDrawingSaved: (document: { localId: number }) => void
  ) {}
  async execute(drawing: DrawingModel, localId: number): Promise<void> {
    debugLog('AddDrawingUseCase', drawing, localId);
    const existingDocument = await this.getDocument(localId);
    const changes = drawingToDocumentChanges(drawing, existingDocument?.drawings);
    await this.updateDrawing(localId, changes);
    this.broadcastDrawingSaved({ localId });

    const currentProject = await this.getCurrentProject();
    await this.addRequest({
      createdAt: Date.now(),
      entityType: HttpRequestModelType.drawing,
      data: { localId, projectId: currentProject?._id, changes },
      method: 'POST',
      status: HttpRequestStatus.NEW,
    });
  }
}

const drawingSaver = new DrawingSaver(
  getDocument,
  updateDrawing,
  addRequest,
  getCurrentProject,
  broadcastCreatedDrawing
);

export default drawingSaver;
